import { IonLoading, useIonActionSheet, useIonAlert } from '@ionic/react';
import React, { useCallback, useMemo } from 'react';

import { Capacitor } from '@capacitor/core';
import { Dialog } from '@capacitor/dialog';

import { TimelineItem, TimelinePlaceholder } from '@/components/dashboard';
import {
  useClinic,
  useDentist,
  useRequests,
  useSubscription
} from '@/contexts';
import { Request } from '@/data/Requests';
import { usePaywall, useToast } from '@/hooks';
import { Paywall } from '@/pages';
import {
  ACCESS_TYPES,
  ATTENDANCE_STATUS_OPTIONS,
  OFF_DUTY_PATIENT_ID,
  REQUEST_ACTIONS,
  REQUEST_RESCHEDULE_METHODS
} from '@/utils/constants';
import { canEditRequestProcedures } from '@/utils/helpers/appointmentPurpose';
import { formatName } from '@/utils/helpers/formatName';
import { parseTime } from '@/utils/helpers/parseTime';
import { PAYWALL_SOURCES } from '@/utils/tracker';
import { RequestAction } from '@/utils/types';
import { isBefore, isEqual } from 'date-fns';

import './Timeline.css';

interface TimelineProps {
  requests: Request[];
  loading?: boolean;
  setRequestToReschedule: (request: Request) => void;
  setRequestToEditProcedures?: (request: Request | null) => void;
}

const Timeline: React.FC<TimelineProps> = ({
  requests,
  loading,
  setRequestToReschedule,
  setRequestToEditProcedures
}) => {
  const [present] = useIonActionSheet();
  const [presentAlert] = useIonAlert();
  const displayToast = useToast();
  const { isSubscribed, purchasing } = useSubscription();
  const { showPaywall, onOpenPaywall, onClosePaywall } = usePaywall();
  const {
    cancelRequestMutation,
    updateAttendanceStatusMutation,
    focusedRequestId,
    updating
  } = useRequests();
  const { selectedDentist: dentist } = useDentist();
  const { isClinic } = useClinic();

  const handleReschedule = async (request: Request) => {
    const isDemoBooking =
      request.patient?.mobile_number === null ||
      request.patient?.mobile_number === dentist?.mobile_number;

    if (isSubscribed === false && !isDemoBooking) {
      onOpenPaywall({
        source: PAYWALL_SOURCES.TIMELINE,
        action: 'reschedule'
      });
      return;
    }

    await presentAlert({
      header: 'Reschedule',
      message: `How would you like to reschedule your appointment with ${formatName(request.patient?.first_name)}?`,
      inputs: [
        {
          type: 'radio',
          label: 'Manually enter a new date and time',
          value: REQUEST_RESCHEDULE_METHODS.MANUAL,
          checked: true
        },
        {
          type: 'radio',
          label:
            'Cancel appointment and send a new booking link to your patient',
          value: REQUEST_RESCHEDULE_METHODS.AUTOMATIC
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Confirm',
          handler: async (selectedMethod: string) => {
            if (selectedMethod === REQUEST_RESCHEDULE_METHODS.MANUAL) {
              setRequestToReschedule(request);
            } else if (
              selectedMethod === REQUEST_RESCHEDULE_METHODS.AUTOMATIC
            ) {
              await handleAction({
                request,
                action: REQUEST_ACTIONS.CANCEL,
                confirm: true
              });
            }
          }
        }
      ]
    });
  };

  const handleAction = useCallback(
    async ({
      request,
      action,
      confirm
    }: {
      request: Request;
      action: RequestAction;
      confirm?: boolean;
    }) => {
      const isDemoBooking =
        request.patient?.mobile_number === null ||
        request.patient?.mobile_number === dentist?.mobile_number;

      if (isSubscribed === false && !isDemoBooking) {
        onOpenPaywall({
          source: PAYWALL_SOURCES.TIMELINE,
          action: action
        });
        return;
      }

      const isOffDuty = request.patient_id === OFF_DUTY_PATIENT_ID;

      if (confirm) {
        const { value: confirmed } = await Dialog.confirm({
          title: 'Confirm',
          message: isOffDuty
            ? 'Are you sure you want to remove the off-duty status?'
            : `Are you sure you want to ${action.toUpperCase()} your appointment with ${formatName(request.patient?.first_name)}?`,
          okButtonTitle: 'Yes'
        });

        if (!confirmed) return;
      }

      try {
        if (action === REQUEST_ACTIONS.CANCEL) {
          if (isOffDuty) {
            cancelRequestMutation.mutate({
              request,
              reason: null
            });
            return;
          }

          const { value: reason, cancelled } = await Dialog.prompt({
            title: `Reason for ${action}`,
            message: 'Please provide a reason or any additional notes',
            inputPlaceholder: 'Enter reason or notes (optional)'
          });
          if (cancelled) return;

          cancelRequestMutation.mutate({
            request,
            reason: reason ?? null
          });
        } else if (
          action === REQUEST_ACTIONS.MARK_AS_LATE ||
          action === REQUEST_ACTIONS.MARK_AS_NO_SHOW
        ) {
          const newStatus =
            action === REQUEST_ACTIONS.MARK_AS_LATE
              ? request.attendance_status === ATTENDANCE_STATUS_OPTIONS.LATE
                ? ATTENDANCE_STATUS_OPTIONS.DEFAULT
                : ATTENDANCE_STATUS_OPTIONS.LATE
              : request.attendance_status === ATTENDANCE_STATUS_OPTIONS.NO_SHOW
                ? ATTENDANCE_STATUS_OPTIONS.DEFAULT
                : ATTENDANCE_STATUS_OPTIONS.NO_SHOW;

          updateAttendanceStatusMutation.mutate({
            request,
            status: newStatus
          });
        }
      } catch {
        displayToast({
          message: 'Something went wrong. Please try again.',
          duration: 5000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    },
    [
      cancelRequestMutation,
      updateAttendanceStatusMutation,
      isSubscribed,
      dentist?.mobile_number
    ]
  );

  const hasAccess = useMemo(() => {
    return isClinic || dentist?.access === ACCESS_TYPES.EDIT;
  }, [isClinic, dentist?.access]);

  const onClick = (request: Request, isPast?: boolean) => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const isOffDuty = request.patient_id === OFF_DUTY_PATIENT_ID;

    present({
      header: `${formatName(request.patient?.first_name)}${isOffDuty ? '-' : ' '}${formatName(request.patient?.last_name)}`,
      subHeader: request.purpose || '',
      buttons: [
        ...(isPast && !isOffDuty
          ? [
              {
                text:
                  request.attendance_status === ATTENDANCE_STATUS_OPTIONS.LATE
                    ? 'Remove Late Status'
                    : 'Mark as Late',
                handler: () =>
                  handleAction({
                    request,
                    action: REQUEST_ACTIONS.MARK_AS_LATE
                  })
              },
              {
                text:
                  request.attendance_status ===
                  ATTENDANCE_STATUS_OPTIONS.NO_SHOW
                    ? 'Remove No-Show Status'
                    : 'Mark as No-Show',
                handler: () =>
                  handleAction({
                    request,
                    action: REQUEST_ACTIONS.MARK_AS_NO_SHOW
                  })
              }
            ]
          : []),
        ...(!hasAccess || (isPast && !isOffDuty)
          ? []
          : [
              {
                text: isOffDuty
                  ? 'Remove Off-Duty Status'
                  : 'Cancel Appointment',
                role: 'destructive',
                handler: () =>
                  handleAction({
                    request,
                    action: REQUEST_ACTIONS.CANCEL,
                    confirm: true
                  })
              }
            ]),
        ...(isOffDuty || !hasAccess || isPast
          ? []
          : [
              {
                text: 'Reschedule',
                handler: () => handleReschedule(request)
              }
            ]),
        ...(hasAccess &&
        canEditRequestProcedures(request) &&
        setRequestToEditProcedures
          ? [
              {
                text: 'Edit Procedures',
                handler: () => setRequestToEditProcedures(request)
              }
            ]
          : []),
        {
          text: 'Nevermind',
          role: 'cancel',
          data: { action: 'cancel' }
        }
      ]
    });
  };

  const renderTimelinePlaceholders = () => {
    return (
      <>
        {[{ isFirst: true }, {}, { isLast: true }].map((props, index) => (
          <TimelinePlaceholder key={index} {...props} />
        ))}
      </>
    );
  };

  const renderTimelineItems = () => {
    return requests.map((request, index) => {
      const isLast = index === requests.length - 1;
      const isDifferentTime =
        !isLast &&
        isBefore(
          parseTime(request.end_time),
          parseTime(requests[index + 1].start_time)
        );
      const isOverlappedStartTime =
        index > 0 &&
        (isBefore(
          parseTime(request.start_time),
          parseTime(requests[index - 1].end_time)
        ) ||
          isEqual(
            parseTime(request.start_time),
            parseTime(requests[index - 1].end_time)
          ));

      return (
        <TimelineItem
          key={request.id}
          request={request}
          onClick={onClick}
          isLast={isLast}
          isDifferentTime={isDifferentTime}
          isOverlappedStartTime={isOverlappedStartTime}
          focusedRequestId={focusedRequestId}
          showEditProcedures={
            hasAccess && canEditRequestProcedures(request)
          }
          onEditProcedures={
            setRequestToEditProcedures
              ? (r) => setRequestToEditProcedures(r)
              : undefined
          }
        />
      );
    });
  };

  return (
    <div className="timeline">
      {loading ? renderTimelinePlaceholders() : renderTimelineItems()}

      <Paywall
        isOpen={showPaywall}
        onClose={onClosePaywall}
        loading={purchasing}
      />

      <IonLoading isOpen={updating} message="Loading..." spinner="crescent" />
    </div>
  );
};

export default Timeline;
